<template>
  <div class="dropdown-wrapper">
    <button
      ref="trigger"
      class="profile-trigger"
      @click.prevent="dropdownOpen = !dropdownOpen"
      aria-haspopup="true"
      :aria-expanded="dropdownOpen"
    >
      <div class="user-avatar">
        {{ userInitials }}
      </div>
      <div class="profile-info">
        <span class="profile-name">{{ userName }}</span>
        <svg class="profile-chevron" viewBox="0 0 12 12">
          <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
        </svg>
      </div>
    </button>
    
    <transition
      enter-active-class="transition ease-out duration-200 transform"
      enter-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-out duration-200"
      leave-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div 
        v-show="dropdownOpen" 
        ref="dropdown"
        class="dropdown-menu"
        :class="align === 'right' ? 'right-0' : 'left-0'"
      >
        <div class="profile-header">
          <div class="profile-header-name">{{ userName }}</div>
          <div class="profile-header-role">{{ userRole }}</div>
        </div>
        <ul class="dropdown-list">
          <li>
            <router-link class="dropdown-link" to="/panel/exports" @click.native="dropdownOpen = false">
              Exports
            </router-link>
          </li>
          <li>
            <router-link class="dropdown-link" to="/change-password" @click.native="dropdownOpen = false">
              Settings
            </router-link>
          </li>
          <li>
            <a class="dropdown-link" @click="logout">Sign Out</a>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'DropdownProfile',
  props: {
    align: {
      type: String,
      default: 'right'
    }
  },
  data() {
    return {
      dropdownOpen: false
    }
  },
  computed: {
    userName() {
      return this.$store.state.Username || 'User'
    },
    userInitials() {
      const username = this.$store.state.Username || 'U'
      return username.charAt(0).toUpperCase()
    },
    userRole() {
      return this.$store.state.IsStaff ? 'Administrator' : 'User'
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    handleClickOutside(e) {
      if (!this.dropdownOpen) return
      if (this.$refs.dropdown && this.$refs.dropdown.contains(e.target)) return
      if (this.$refs.trigger && this.$refs.trigger.contains(e.target)) return
      this.dropdownOpen = false
    },
    handleKeyDown(e) {
      if (!this.dropdownOpen || e.keyCode !== 27) return
      this.dropdownOpen = false
    },
    logout() {
      this.$store.commit('logout')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles-user-dashboard.scss';

// Transition classes
.transition {
  transition-property: all;
}

.ease-out {
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}

.duration-200 {
  transition-duration: 200ms;
}

.transform {
  transform: translateZ(0);
}

.opacity-0 {
  opacity: 0;
}

.opacity-100 {
  opacity: 1;
}

.translate-y-0 {
  transform: translateY(0);
}

.-translate-y-2 {
  transform: translateY(-0.5rem);
}

.dropdown-wrapper {
  position: relative;
  display: inline-flex;
}

.profile-trigger {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  gap: 8px;
  
  &:hover {
    opacity: 0.9;
  }
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--v-primary-base) 88%, white),
    color-mix(in srgb, var(--v-primary-base) 78%, black)
  );
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.profile-name {
  font-size: 14px;
  font-weight: 500;
  color: $gray-600;
  
  .theme--dark & {
    color: $gray-100;
  }
}

.profile-chevron {
  width: 12px;
  height: 12px;
  fill: currentColor;
  color: $gray-400;
  
  .theme--dark & {
    color: $gray-500;
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  z-index: 50;
  min-width: 176px;
  background-color: white;
  border: 1px solid $gray-200;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  padding: 6px 0;
  
  .theme--dark & {
    background-color: $gray-800;
    border-color: rgba($gray-700, 0.6);
  }
}

.profile-header {
  padding: 8px 12px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid $gray-200;
  
  .theme--dark & {
    border-bottom-color: rgba($gray-700, 0.6);
  }
}

.profile-header-name {
  font-weight: 500;
  font-size: 14px;
  color: $gray-800;
  
  .theme--dark & {
    color: $gray-100;
  }
}

.profile-header-role {
  font-size: 12px;
  color: $gray-500;
  font-style: italic;
  margin-top: 2px;
  
  .theme--dark & {
    color: $gray-400;
  }
}

.dropdown-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-link {
  display: block;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  color: var(--v-primary-base);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
  
  &:hover {
    color: color-mix(in srgb, var(--v-primary-base) 82%, black);
    
    .theme--dark & {
      color: color-mix(in srgb, var(--v-primary-base) 82%, white);
    }
  }
}
</style>
